import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { NafCode } from './NafCodeSelector';
import { 
  selectCurrentView, 
  LinkedAdherent, 
  selectError, 
  ProspectionViewValues,
  selectSortBy,
  selectSortOrder,
  setSorting
} from '../../store/slices/nouveauxAdherentsSlice';
import LinkedAdherentsPopup from './LinkedAdherentsPopup';
import { Establishment } from './EstablishmentTypes';
import { useTableUtils } from './tableComponents/useTableUtils';
import {
  StandardHeaders,
  SectorHeaders,
  NonClientFromClientHeaders,
  StandardEstablishmentRow,
  InSectorEstablishmentRow,
  OutOfSectorEstablishmentRow,
  NonClientFromClientRow
} from './tableComponents';

interface EnhancedEstablishmentsTableProps {
  establishments: Establishment[] | undefined;
  nafCodes: NafCode[];
  isLoading: boolean;
  onSortChange?: () => void;
}

const EnhancedEstablishmentsTable: React.FC<EnhancedEstablishmentsTableProps> = ({
  establishments,
  nafCodes,
  isLoading,
  onSortChange,
}) => {
  const dispatch = useDispatch();
  const currentView = useSelector(selectCurrentView);
  const error = useSelector(selectError);
  const currentSortBy = useSelector(selectSortBy);
  const currentSortOrder = useSelector(selectSortOrder);
  const [selectedAdherents, setSelectedAdherents] = useState<LinkedAdherent[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [lastViewRendered, setLastViewRendered] = useState(currentView);
  const utils = useTableUtils(nafCodes);
  
  // Track view changes to ensure proper cleanup
  useEffect(() => {
    if (currentView !== lastViewRendered) {
      setShowPopup(false);
      setSelectedAdherents([]);
      setLastViewRendered(currentView);
    }
  }, [currentView, lastViewRendered]);
  
  // Handler for sorting
  const handleSort = (sortBy: string, sortOrder: string) => {
    dispatch(setSorting({ sortBy, sortOrder }));
    if (onSortChange) {
      onSortChange();
    }
  };
  
  // Handler for linked adherents button
  const handleShowLinkedAdherents = (adherents: LinkedAdherent[]) => {
    setSelectedAdherents(adherents);
    setShowPopup(true);
  };

  // Close popup handler
  const handleClosePopup = () => {
    setShowPopup(false);
  };

  // Get headers for current view
  const renderHeaders = () => {
    const headerProps = {
      currentSortBy,
      currentSortOrder,
      onSort: handleSort,
    };

    switch (currentView) {
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR:
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR:
        return <SectorHeaders {...headerProps} />;
      case ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES:
        return <NonClientFromClientHeaders {...headerProps} />;
      default:
        return <StandardHeaders {...headerProps} />;
    }
  };

  // Render a row based on the current view
  const renderRow = (establishment: Establishment) => {
    switch (currentView) {
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR:
        return <InSectorEstablishmentRow establishment={establishment} utils={utils} />;
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR:
        return <OutOfSectorEstablishmentRow establishment={establishment} utils={utils} />;
      case ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES:
        return <NonClientFromClientRow 
                 establishment={establishment} 
                 utils={utils}
                 handleShowLinkedAdherents={handleShowLinkedAdherents} 
               />;
      default:
        return <StandardEstablishmentRow establishment={establishment} utils={utils} />;
    }
  };

  const getColSpan = () => {
    switch (currentView) {
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR:
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR:
        return 6;
      case ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES:
        return 6;
      default:
        return 4;
    }
  };

  // Render content for table body
  const renderTableContent = () => {
    // Always show loading animation when isLoading is true
    if (isLoading) {
      return (
        <tr>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={getColSpan()}>
            <div className="flex justify-center">
              <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          </td>
        </tr>
      );
    }

    // Show error message if there's an error
    if (error) {
      return (
        <tr>
          <td className="px-6 py-8 whitespace-nowrap text-sm text-gray-500" colSpan={getColSpan()}>
            <div className="flex flex-col items-center justify-center">
              <ExclamationTriangleIcon className="h-10 w-10 text-red-500 mb-2" />
              <p className="text-center font-medium">Une erreur est survenue lors de la récupération des données</p>
              <p className="text-center text-gray-500 mt-1">{error}</p>
              <p className="text-center text-gray-500 mt-4">Essayez de rafraîchir la page ou de modifier vos critères de recherche</p>
            </div>
          </td>
        </tr>
      );
    }
    
    // If establishments is undefined, it means we haven't loaded data yet
    // show loading indicator instead of "no results" message
    if (establishments === undefined) {
      return (
        <tr>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={getColSpan()}>
            <div className="flex justify-center">
              <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          </td>
        </tr>
      );
    }

    // Show establishments if we have any
    if (establishments.length > 0) {
      return establishments.map((establishment, index) => {
        // Use view + siret + index to ensure unique keys even with duplicate SIRETs in same view
        return (
          <tr key={`${currentView}-${establishment.siret}-${index}`}>
            {renderRow(establishment)}
          </tr>
        );
      });
    }

    // Show "no results" message - at this point establishments is defined but empty array
    return (
      <tr>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={getColSpan()}>
          Aucun établissement trouvé pour ces critères.
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {renderHeaders()}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {renderTableContent()}
        </tbody>
      </table>
      
      {showPopup && (
        <LinkedAdherentsPopup 
          adherents={selectedAdherents} 
          onClose={handleClosePopup} 
        />
      )}
    </div>
  );
};

export default EnhancedEstablishmentsTable; 